import * as Dialog from "@radix-ui/react-dialog";
import * as Yup from "yup";
import { Field, FieldInputProps, FieldMetaProps, Form, Formik } from "formik";
import { useEffect, useRef, useState } from "react";
import { ReactComponent as CloseIcon } from "../assets/svg/x-close.svg";
import { baseQueryAPI } from "../services/baseQuery";
import { assignArea, editArea } from "../services/api";
import { Spinner } from "./ui/Spinner";

export const EditArea = ({
  area,
  refetch,
  closeDrawing,
  onclose,
}: {
  area: any;
  refetch: any;
  closeDrawing: any;
  onclose: () => void;
}) => {
  const [team, setTeam] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState<string>("");
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [showOptions, setShowOptions] = useState(false);
  const searchContainerRef = useRef<any>();

  const { data: myTeamData } = baseQueryAPI.useMyTeamQuery("");

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      if (inputValue) {
        handleInputChange(inputValue);
      }
    }, 300); // 300ms delay

    return () => clearTimeout(delayDebounce);
  }, [inputValue]);

  useEffect(() => {
    if (myTeamData) {
      setTeam(myTeamData.data);
      setInputValue(
        myTeamData.data.find((itm: any) => itm.public_id === area.user_id)?.name
      );
    }
  }, [myTeamData]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        searchContainerRef.current &&
        !searchContainerRef.current.contains(event?.target as Node)
      ) {
        setShowOptions(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [searchContainerRef]);

  const handleInputChange = (value: string) => {
    setInputValue(value);
    const lowerCased = value.toLowerCase();
    setTeam(
      myTeamData.data.filter(
        (itm: any) => !value || itm.name.toLowerCase().includes(lowerCased)
      )
    );
  };

  const handleClose = () => {
    setErrorMessage("");
    refetch();
    onclose();
  };

  async function handleSave(val: any) {
    console.log({ val });
    setLoading(true);
    const params = {
      ...val,
      area_cordinates: JSON.stringify(area.area_cordinates),
    };
    try {
      console.log({ params });
      const result = await editArea(params, area.area_id);
      if (result?.data.success) {
        handleClose();
      }
    } catch (error) {
      console.error(error);
      setErrorMessage("Something went wrong! Try again");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div>
      <Dialog.Root open={true}>
        <Dialog.Portal>
          <Dialog.Overlay className="bg-gray-800 bg-opacity-60 fixed inset-0 z-40" />
          <Dialog.Content
            style={{ transform: "translate(-50%, -50%)" }}
            className="fixed left-1/2 top-1/2 bg-white p-6 z-40 min-w-[30rem]"
          >
            <Dialog.Title className="text-gray-900 text-lg font-semibold">
              Edit Area
            </Dialog.Title>

            <Dialog.Close>
              <button
                onClick={handleClose}
                type="button"
                className="absolute top-6 right-6"
              >
                <CloseIcon />
              </button>
            </Dialog.Close>

            <Formik
              initialValues={{
                area_name: area.area_name,
                user_id: area.user_id,
              }}
              onSubmit={async (val: any) => {
                handleSave(val);
              }}
              validationSchema={Yup.object({
                user_id: Yup.string().required("Please choose a team member"),
              })}
            >
              {({ setFieldValue }) => (
                <Form className="space-y-3">
                  <div className="flex flex-col gap-y-3">
                    <label className="text-sm font-medium text-gray-700">
                      Team Member
                    </label>
                    <Field name="user_id">
                      {({
                        field,
                        meta,
                      }: {
                        field: FieldInputProps<any>;
                        meta: FieldMetaProps<any>;
                      }) => (
                        <div ref={searchContainerRef} className="relative">
                          <input
                            className="border px-4 py-2 outline-none w-full"
                            type="text"
                            autoComplete="off"
                            {...field}
                            value={inputValue}
                            onChange={(e) => handleInputChange(e.target.value)}
                            onClick={() => setShowOptions(true)}
                          />

                          {showOptions ? (
                            <div className="absolute w-full z-30 max-h-80 overflow-auto mt-1 shadow-md border border-t-0">
                              <ul className="bg-white shadow-md">
                                {team.map((suggestion: any, index: number) => (
                                  <li
                                    key={"suggestion_" + index}
                                    className="p-4 hover:bg-blue-400 hover:text-white"
                                  >
                                    <button
                                      className="font-medium w-full text-start"
                                      type="button"
                                      onClick={() => {
                                        setFieldValue(
                                          "user_id",
                                          suggestion.public_id
                                        );
                                        setInputValue(suggestion.name);
                                        setShowOptions(false);
                                      }}
                                    >
                                      <span className="line-clamp-1">
                                        {suggestion.name}
                                      </span>
                                    </button>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          ) : null}
                          {meta.touched && meta.error && (
                            <div className="text-xs text-red-500 mt-2">
                              {meta.error}
                            </div>
                          )}
                        </div>
                      )}
                    </Field>
                  </div>
                  <div className="flex flex-col gap-y-3">
                    <label className="text-sm font-medium text-gray-700">
                      Area Name
                    </label>
                    <Field name="area_name">
                      {({
                        field,
                        meta,
                      }: {
                        field: FieldInputProps<any>;
                        meta: FieldMetaProps<any>;
                      }) => (
                        <div>
                          <input
                            className="border px-4 py-2 outline-none w-full"
                            type="text"
                            {...field}
                          />
                          {meta.touched && meta.error && (
                            <div className="text-xs text-red-500 mt-2">
                              {meta.error}
                            </div>
                          )}
                        </div>
                      )}
                    </Field>
                  </div>

                  {errorMessage ? (
                    <div className="p-3 bg-red-400 text-white mt-4">
                      {errorMessage}
                    </div>
                  ) : null}

                  <div className="mt-6 flex w-full justify-end gap-x-4">
                    <button
                      onClick={handleClose}
                      className="px-4 py-2 border"
                      type="button"
                    >
                      Close
                    </button>
                    <button
                      className="px-4 py-2 bg-primary text-white flex gap-2"
                      type="submit"
                      disabled={loading}
                    >
                      {loading ? <Spinner /> : null}
                      Save
                    </button>
                  </div>
                </Form>
              )}
            </Formik>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </div>
  );
};
